import { type Ref, type ComputedRef, ref, computed } from "vue";

import { graphGet } from "@/helpers/api/apiGraph";
import DriveItem from "@/models/Documents/DriveItem";

import FileHelper from "@/helpers/utilities/FileHelper";
import GraphHelper from "@/helpers/utilities/GraphHelper";
import apiSkobo from "@/helpers/api/apiSkobo";

interface SharedInsightItem {
  id: string;
  resourceReference?: { type?: string; id?: string };
  lastShared?: { sharedBy?: { id?: string } & Record<string, unknown> };
}

interface BatchResponseEntry {
  id: string;
  status: number;
  body?: unknown;
}

interface RecentShareItem {
  id: number;
  graphDriveId: string;
  graphItemId: string;
  sharedWithUserId: string;
  sharedByUserId: string;
  customerId: string;
  createdDate: string;
}

interface MyFilesDependencies {
  driveMap: Ref<Record<string, Record<string, DriveItem>>>;
  // Lazy lookups for symbols still defined in the confidentialDocuments
  // section of index.ts.
  getLoadHiddenDriveItems: () => () => Promise<void>;
  hiddenSharedFiles: ComputedRef<Array<{ driveId: string; itemId: string }>>;
}

const loadSharedResourcesBatch = async (idsToLoad: string[]): Promise<BatchResponseEntry[]> => {
  const expandBatchRequests = idsToLoad.map((id: string) => ({
    id,
    method: "GET",
    url: `${id}`
  }));
  const response = (await GraphHelper.makeBatchRequest(expandBatchRequests)) as BatchResponseEntry[];
  const okResponses = response.filter(entry => entry.status === 200);
  const busyResponseIds = response.filter(entry => entry.status === 429).map(entry => entry.id);
  if (busyResponseIds.length > 0) {
    await new Promise(resolve => setTimeout(resolve, 6000));
    const retryResponses = await loadSharedResourcesBatch(busyResponseIds);
    okResponses.push(...retryResponses);
  }
  return okResponses;
};

export const useMyFiles = (dependencies: MyFilesDependencies) => {
  const oneDriveRootState: Ref<DriveItem | null> = ref(null);
  const driveItemsSharedWithMeState: Ref<DriveItem[] | null> = ref(null);

  const oneDriveRoot = computed(() => oneDriveRootState.value);
  const driveItemsSharedWithMe = computed(() => driveItemsSharedWithMeState.value);

  const reloadMyOneDrive = async () => {
    const response = await graphGet("/me/drive/root?$expand=children");
    const driveItem = new DriveItem(response);

    driveItem.nctSystem = "onedrive";
    driveItem.nctGroup = "onedrive";
    driveItem.nctType = "onedrive";
    driveItem.nctIdentifier = "onedrive";
    driveItem.nctBehavior = "onedrive";

    driveItem.childrenList?.forEach((child: DriveItem) => {
      child.nctSystem = "onedrive";
      child.nctGroup = "onedrive";
      child.nctType = "onedrive";
      child.nctIdentifier = "onedrive";
      child.nctBehavior = "onedrive";
    });

    oneDriveRootState.value = driveItem;

    if (!dependencies.driveMap.value[driveItem.parentReference.driveId]) {
      dependencies.driveMap.value[driveItem.parentReference.driveId] = {};
    }
    dependencies.driveMap.value[driveItem.parentReference.driveId][driveItem.id] = driveItem;
    return driveItem;
  };

  const loadMyOneDrive = async () => {
    if (!oneDriveRootState.value) {
      await reloadMyOneDrive();
    }
    return oneDriveRootState.value;
  };

  const loadDriveItemsSharedWithMe = async () => {
    const [itemsSharedWithMe, { data: recentShares }] = (await Promise.all([
      graphGet(`/me/insights/shared`),
      apiSkobo.get("/Onedrive/SharedWithMe")
    ])) as [{ value: SharedInsightItem[] }, { data: RecentShareItem[] }];

    const insightsIds = itemsSharedWithMe.value
      .filter((insight: SharedInsightItem) => insight.resourceReference?.type === "microsoft.graph.driveItem")
      .map((insight: SharedInsightItem) => insight.resourceReference!.id as string);

    const recentIds = recentShares.map(
      (recent: RecentShareItem) => `drives/${recent.graphDriveId}/items/${recent.graphItemId}`
    );

    const insightsIdSet = new Set(insightsIds);
    const overlappingShares = recentShares.filter((recent: RecentShareItem) =>
      insightsIdSet.has(`drives/${recent.graphDriveId}/items/${recent.graphItemId}`)
    );

    overlappingShares.forEach((share: RecentShareItem) => {
      apiSkobo
        .delete(
          `/Onedrive/SharedWithMe/${share.graphDriveId}/${share.graphItemId}/${share.sharedWithUserId}/${share.sharedByUserId}`
        )
        .catch(() => {});
    });

    const sharedItemIdsToExpand = Array.from(new Set([...insightsIds, ...recentIds]));
    const okResponses = await loadSharedResourcesBatch(sharedItemIdsToExpand);

    const findSharedBy = (responseId: string) => {
      const fromInsights = itemsSharedWithMe.value.find((insight: SharedInsightItem) => insight.id === responseId)
        ?.lastShared?.sharedBy;
      if (fromInsights?.id) return fromInsights;

      const recentShare = recentShares.find(
        (recent: RecentShareItem) => `drives/${recent.graphDriveId}/items/${recent.graphItemId}` === responseId
      );
      if (recentShare) {
        return { id: recentShare.sharedByUserId, extSysApiUserId: recentShare.sharedByUserId };
      }
      return null;
    };

    const driveItemsSharedWithMeList = okResponses.map((response: BatchResponseEntry) => {
      const newDoc = new DriveItem(response.body) as DriveItem & { shared?: unknown };
      newDoc.remoteItem = new DriveItem(newDoc);

      const sharedByUser = findSharedBy(response.id);
      if (sharedByUser?.id) {
        (newDoc.remoteItem as DriveItem & { shared?: unknown }).shared = { sharedBy: { user: sharedByUser } };
      }
      return newDoc;
    });

    const loadHiddenDriveItems = dependencies.getLoadHiddenDriveItems();
    await loadHiddenDriveItems();

    const filteredList = driveItemsSharedWithMeList.filter(
      (driveItem: DriveItem) =>
        !dependencies.hiddenSharedFiles.value?.some(
          (hidden: { driveId: string; itemId: string }) =>
            driveItem.id === hidden.itemId && driveItem.parentReference.driveId === hidden.driveId
        )
    );

    driveItemsSharedWithMeState.value = FileHelper.addExtensionsToArrayOfFiles(filteredList);
  };

  return {
    oneDriveRootState,
    driveItemsSharedWithMeState,
    oneDriveRoot,
    driveItemsSharedWithMe,
    loadMyOneDrive,
    reloadMyOneDrive,
    loadDriveItemsSharedWithMe
  };
};
